/**
 * Riding through a deploy (docs/plans/deploy-downtime-2026-11-04.md §A5.3; Ken ruled group 3, 11-04).
 *
 * While the back end is being updated, nginx answers every engine route itself with a 503 and the body
 * `{"error":{"code":"deploying"}}`: PHP never ran, so ANY request can be sent again. Other failures are ambiguous (the
 * request may have been carried out), so only routes that carry an Idempotency-Key and replay the stored answer are
 * sent again after one. The per-route table is in docs/tokenforge/engine-contract (the "Retries" section).
 *
 *  - `deploying`: wait 3–5 s (jittered) and send again, with no hard give-up. After 4 minutes the status turns to
 *    "stalled": the page keeps saying it is updating and offers "Try again" (which sends at once).
 *  - ambiguous (no answer at all, 500, 502, 504, Cloudflare 520–524): `keyed` routes only, up to 3 more times.
 *  - an app 503 (`busy`, `service_unavailable`), a 409 or a 429: never sent again here. The server said no or slow
 *    down; a retry would only add load (since .268 a replay spends only the replay window, never the cart windows).
 *  - 409 `approve_in_progress` on approve: another request holds the 5-minute lease, so it is a status poll, not a
 *    failure: "still adding to your cart…", sent again (same key, same bytes) until it answers or the lease runs out.
 *
 * A retry sends the SAME request: the caller builds the body string once and `attempt` sends exactly those bytes and the
 * same Idempotency-Key (the server compares sha256 of the raw body).
 */

export type RetryRoute =
    /** Carries an Idempotency-Key the server replays: safe to send again after an ambiguous failure. */
    | 'keyed'
    /** Everything else: sent again only on `deploying`, which proves the server never ran it. */
    | 'unkeyed';

export type UpdateStatus =
    | { kind: 'idle' }
    /** Waiting out a deploy; `stalled` after 4 minutes (the page then offers "Try again"). */
    | { kind: 'updating'; stalled: boolean }
    /** An approve waiting on another request's lease. */
    | { kind: 'adding' };

export const DEPLOY_STALL_MS = 4 * 60 * 1000;
export const APPROVE_LEASE_MS = 5 * 60 * 1000;
const AMBIGUOUS_RETRIES = 3;
const STALLED_WAIT_MS = 15_000;

// ── the status the page shows ──────────────────────────────────────────────────────

let status: UpdateStatus = { kind: 'idle' };
const listeners = new Set<(s: UpdateStatus) => void>();
const waiting = new Map<number, UpdateStatus>();
let nextWaiter = 0;
let wakeAll: Array<() => void> = [];

function publish(): void {
    const all = [...waiting.values()];
    const next: UpdateStatus = all.some((s) => s.kind === 'updating')
        ? { kind: 'updating', stalled: all.some((s) => s.kind === 'updating' && s.stalled) }
        : all.some((s) => s.kind === 'adding') ? { kind: 'adding' } : { kind: 'idle' };
    if (JSON.stringify(next) !== JSON.stringify(status)) {
        status = next;
        listeners.forEach((l) => l(status));
    }
}

export function updateStatus(): UpdateStatus {
    return status;
}

export function onUpdateStatus(listener: (s: UpdateStatus) => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** "Try again": every request waiting out a deploy is sent at once. */
export function retryNow(): void {
    const wake = wakeAll;
    wakeAll = [];
    wake.forEach((w) => w());
}

// ── the loop ───────────────────────────────────────────────────────────────────────

export interface RetryClock {
    now(): number;
    /** Resolves after `ms`, or at once on {@link retryNow} or an abort. */
    sleep(ms: number, signal?: AbortSignal): Promise<void>;
    random(): number;
}

const realClock: RetryClock = {
    now: () => Date.now(),
    random: () => Math.random(),
    sleep: (ms, signal) =>
        new Promise<void>((resolve) => {
            const done = (): void => {
                clearTimeout(t);
                signal?.removeEventListener('abort', done);
                resolve();
            };
            const t = setTimeout(done, ms);
            wakeAll.push(done);
            signal?.addEventListener('abort', done, { once: true });
        }),
};

let clock: RetryClock = realClock;

/** Tests only: a clock that does not wait. */
export function setRetryClockForTest(c: RetryClock | null): void {
    clock = c ?? realClock;
}

const AMBIGUOUS_STATUS = new Set([500, 502, 504, 520, 521, 522, 523, 524]);

async function errorCode(res: Response): Promise<string | null> {
    try {
        const body = (await res.clone().json()) as { error?: { code?: unknown } };
        return typeof body.error?.code === 'string' ? body.error.code : null;
    } catch {
        return null;
    }
}

/** No answer reached the page (client.ts `unreachable`: an EngineError `network`). Duck-typed: client.ts imports this file. */
function isNoAnswer(e: unknown): boolean {
    return typeof e === 'object' && e !== null && (e as { code?: unknown }).code === 'network';
}

function jitter(): number {
    return 3000 + Math.floor(clock.random() * 2000);
}

/**
 * Send `attempt` until it has an answer worth returning (see the file comment). `attempt` sends the same bytes every
 * time and either returns the Response or throws (EngineError `network` when nothing came back). Whatever
 * is finally returned or thrown is the caller's to read, exactly as without this wrapper.
 */
export async function withRetry(
    route: RetryRoute,
    attempt: () => Promise<Response>,
    opts: { signal?: AbortSignal; approve?: boolean } = {},
): Promise<Response> {
    const id = nextWaiter++;
    let deployingSince: number | null = null;
    let leaseSince: number | null = null;
    let ambiguous = 0;
    try {
        for (;;) {
            let res: Response;
            try {
                res = await attempt();
            } catch (e) {
                if (opts.signal?.aborted !== true && route === 'keyed' && isNoAnswer(e) && ambiguous < AMBIGUOUS_RETRIES) {
                    ambiguous++;
                    await clock.sleep(jitter(), opts.signal);
                    continue;
                }
                throw e;
            }
            if (opts.signal?.aborted === true) {
                return res;
            }

            if (res.status === 503 && (await errorCode(res)) === 'deploying') {
                deployingSince ??= clock.now();
                const stalled = clock.now() - deployingSince >= DEPLOY_STALL_MS;
                waiting.set(id, { kind: 'updating', stalled });
                publish();
                await clock.sleep(stalled ? STALLED_WAIT_MS : jitter(), opts.signal);
                continue;
            }
            if (opts.approve === true && res.status === 409 && (await errorCode(res)) === 'approve_in_progress') {
                leaseSince ??= clock.now();
                if (clock.now() - leaseSince < APPROVE_LEASE_MS) {
                    waiting.set(id, { kind: 'adding' });
                    publish();
                    await clock.sleep(jitter(), opts.signal);
                    continue;
                }
                return res;
            }
            if (route === 'keyed' && AMBIGUOUS_STATUS.has(res.status) && ambiguous < AMBIGUOUS_RETRIES) {
                ambiguous++;
                await clock.sleep(jitter(), opts.signal);
                continue;
            }
            return res;
        }
    } finally {
        waiting.delete(id);
        publish();
    }
}
